import config from "@config";
import { loadLanguageAsync } from "@storefront/core/i18n";
import { Logger } from "@storefront/core/lib/logger";
import {
  cleanPath,
  cleanFullPath,
  extractFilters,
} from "@storefront/core/router/helpers/cleanPath.js";
import { router } from "@/main";
import { urlRoute } from "@storefront/core/data-resolver/url";
import store from "@/store";

function setSSRStatusCode(router, statusCode, redirectPath = null) {
  if (typeof window !== 'undefined') return;

  const ssrContext = router.ssrContext || (router.app && router.app.$ssrContext) || router.options?.ssrContext;

  if (!ssrContext) {
    return;
  }

  ssrContext.statusCode = statusCode;

  if (redirectPath) {
    ssrContext.redirectUrl = redirectPath;
  }

  if (ssrContext.res) {
    ssrContext.res.statusCode = statusCode;
    if (redirectPath && statusCode === 301) {
      ssrContext.res.setHeader('Location', redirectPath);
      ssrContext.res.end();
      return true;
    }
  }

  if (ssrContext.ssr) {
    ssrContext.ssr.statusCode = statusCode;
  }

  return false;
}

export async function beforeEachGuard(to, from, next, _router) {
  //check language
  let lang = "";
  const split = to.path.split("/");
  if (config.languages.available.includes(split[1])) {
    lang = await loadLanguageAsync(split[1]).catch((e) => {
      Logger.error("loadLanguageAsync1", "beforeEachGuard", e)();
      throw e;
    });
  } else {
    lang = await loadLanguageAsync(config.languages.default).catch((e) => {
      Logger.error("loadLanguageAsync2", "beforeEachGuard", e)();
      throw e;
    });
  }
  const path = cleanPath(to); /// remove lang
  const fullpath = cleanFullPath(to); // Replace any occurrences of "//" with "/"
  //let resolve = null;

  if (to.path === from.path) return next();
  var newRoute = path;
  let newFilter = [];
  if (path && path.indexOf(":") != -1) {
    let routeParts = path.split("/");
    if (routeParts && routeParts.length) {
      var splitIndex = 0;
      routeParts.forEach((part, index) => {
        if (splitIndex > 0) return;
        if (part.includes(":")) {
          splitIndex = index;
          return false;
        }
      });
      newRoute = routeParts.slice(0, splitIndex).join("/");
      if (!newRoute.startsWith("/")) {
        newRoute = "/" + newRoute;
      }
      const filter_name = "/" + config.filter_name + "/";
      if (newRoute.endsWith("/f")) {
        newRoute = newRoute.slice(0, -2);
      }
      if (newRoute.endsWith(filter_name)) {
        newRoute = newRoute.slice(0, -filter_name.length);
      }

      if (!newRoute.endsWith("/")) {
        newRoute += "/";
      }

      newRoute = newRoute.replace(/\/{2,}/g, "/");

      const filterArr = routeParts.slice(splitIndex);
      newFilter = filterArr.filter((el) => el !== "");
    }
  }
  let resolve = null;
  try {
    resolve = await urlRoute(newRoute);
  } catch (e) {
    Logger.error("urlResolver", "beforeEachGuard", e)();
  }

  if (resolve && resolve.redirect_code === 301 && resolve.relative_url && newRoute !== "/") {
    const targetPath = `/${resolve.relative_url.replace(/^\//g, "")}`;

    setSSRStatusCode(_router, 301, targetPath);

    return next(targetPath);

  }

  if (resolve != null) {
    //Load the information per type of route
    switch (resolve.type) {
      case "CATEGORY": {
        let load = true;
        const obj = store.getters["category/getCurrentCategory"];
        if (obj != null && obj.id == resolve.id) {
          load = false;
        }
        if (load) {
          newFilter.category = resolve.id;
          const filter = await extractFilters(to, newFilter);
          store.dispatch("category/setCategory", {
            category: resolve,
            filter: filter,
          });
        }
        break;
      }
      case "PRODUCT": {
        let load = true;
        const obj = store.getters["product/getCurrentProduct"];
        if (obj != null) {
          if (obj.sku == resolve.sku) {
            load = false;
          }
        }
        if (load) {
          await store
            .dispatch("product/loadWithSku", {
              sku: resolve.sku,
            })
            .catch((e) => {
              Logger.error("product/loadWithSku", "beforeEachGuard", e)();
              throw e;
            });
        }
        break;
      }
      case "CMS_PAGE": {
        store.dispatch("cmsPage/setPage", {
          page: resolve,
        });

        break;
      }
      case "LANDING_PAGE": {
        store.dispatch("landingPage/getMeta", {
          meta: resolve,
        });

        break;
      }
    }
  }

  if (to.matched.length === 0) {
    const testRoute = router.options.routes.find((e) => {
      if (e.path === path) {
        return true;
      }
    });
    if (testRoute != null) {
      router.addRoutes({
        path: to.path,
        component: testRoute.component,
      });
      // next(to.fullPath);
      next(fullpath);
      return;
    }
    if (resolve != null) {
      let route = false;
      switch (resolve.type) {
        case "CATEGORY": {
          const testPage = router.options.routes.find(
            (e) => e.name === "category-page"
          );
          if (testPage != null) {
            route = {
              name: "category-" + lang + "-" + path.replace("/", "-"),
              path: to.path,
              component: testPage.component,
            };
          }
          break;
        }
        case "PRODUCT": {
          const testPage = router.options.routes.find(
            (e) => e.name === "product-page"
          );
          if (testPage != null) {
            route = {
              name: "product-" + lang + "-" + path.replace("/", "-"),
              path: to.path,
              component: testPage.component,
            };
          }
          break;
        }
        case "CMS_PAGE": {
          const testPage = router.options.routes.find(
            (e) => e.name === "cms-page"
          );
          if (testPage != null) {
            route = {
              name: "cms-" + lang + "-" + path.replace("/", "-"),
              path: to.path,
              component: testPage.component,
            };
          }
          break;
        }
        case "LANDING_PAGE": {
          const testPage = router.options.routes.find(
            (e) => e.name === "landing-page"
          );
          if (testPage != null) {
            route = {
              name: "landing-" + lang + "-" + path.replace("/", "-"),
              path: to.path,
              component: testPage.component,
            };
          }
          break;
        }
      }
      if (route !== false) {
        router.addRoute(route);
        // return next(to.fullPath);
        return next(fullpath);
      }
      return next();
    }
  } else {
    if (store.getters["user/getIsLoggedIn"] == true) {
      if (to.name === "login") {
        return next({
          name: "account",
        });
      } else if (to.name === "create-account") {
        return next({
          name: "account",
        });
      } else if (to.name === "en-login") {
        return next({
          name: "en-account",
        });
      } else if (to.name === "en-create-account") {
        return next({
          name: "en-account",
        });
      }
    } else {
      if (to.name === "account") {
        return next({
          name: "login",
        });
      } else if (to.name === "en-account") {
        return next({
          name: "en-login",
        });
      }
    }
    // the route and component are known and data is available

    return next();
  }

  setSSRStatusCode(_router, 404);
  const pageNotFoundRoute = router.options.routes.find((e) => e.name === "page-not-found");
  if (pageNotFoundRoute) {
    router.addRoute({
      name: '404-' + to.path,
      path: to.path,
      component: pageNotFoundRoute.component
    });
    return next(to.fullPath);
  }
  return next("/page-not-found");
}
